import React, { useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { Download, Loader2, Wallet } from 'lucide-react';
import { useWallet } from '@/context/WalletContext';
import { shortHex } from '@/lib/bindingConsent';

const API = process.env.REACT_APP_BACKEND_URL;
const kitMessage = (rid, t) => `Kojiru defense kit request\nReceipt: ${rid.toLowerCase()}\nIssued: ${t}`;

function save(kit) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(kit, null, 2)], { type: 'application/json' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: `kojiru-defense-kit-${kit.receipt_id.slice(0, 10)}.json` });
  a.click();
  URL.revokeObjectURL(url);
}

export function DefenseKitButton({ receipt }) {
  const { address, isConnected } = useAccount();
  const { connectWallet } = useWallet();
  const { signMessageAsync } = useSignMessage();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const me = address?.toLowerCase();
  const isSigner = isConnected && (me === receipt.principal.toLowerCase() || (receipt.operator_wallet && me === receipt.operator_wallet.toLowerCase()));
  const anchored = receipt.anchor?.status === 'anchored';

  const download = async () => {
    setBusy(true); setMsg(null);
    try {
      const t = Math.floor(Date.now() / 1000);
      const signature = await signMessageAsync({ message: kitMessage(receipt.receipt_id, t) });
      const r = await fetch(`${API}/api/binding/receipts/${receipt.receipt_id}/defense-kit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ signature, issued_at: t }) });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || 'Download failed');
      save(body);
      setMsg({ ok: true, text: 'Defense kit downloaded. Keep it private: it contains the receipt signature.' });
    } catch (e) {
      setMsg({ ok: false, text: e?.shortMessage || e?.message || 'Download failed' });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5" data-testid="defense-kit-card">
      <div className="text-sm font-semibold text-white mb-1">Receipt evidence kit</div>
      <p className="text-xs text-gray-400 mb-3 leading-relaxed">Everything needed to prove this receipt to any third party: the signed receipt fields, the signature, the anchor day, and the Merkle proof. Only the wallet that signed this receipt, or the agent's operator wallet, can download it. The request is a free signature, no gas.</p>
      {!anchored ? (
        <p className="text-xs text-amber-300" data-testid="defense-kit-pending">Available after this receipt's daily Base anchor.</p>
      ) : !isConnected ? (
        <button type="button" onClick={connectWallet} className="inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors" data-testid="defense-kit-connect"><Wallet className="w-3.5 h-3.5" /> Connect signer wallet</button>
      ) : !isSigner ? (
        <p className="text-xs text-amber-300" data-testid="defense-kit-wrong-wallet">Connected {shortHex(address)}. Only the signer {shortHex(receipt.principal)}{receipt.operator_wallet ? ` or the operator ${shortHex(receipt.operator_wallet)}` : ''} can download this kit.</p>
      ) : (
        <button type="button" onClick={download} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-50 px-4 py-2 text-xs font-semibold text-white transition-colors" data-testid="defense-kit-download">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download evidence kit
        </button>
      )}
      {msg && <p className={`text-xs mt-2 ${msg.ok ? 'text-emerald-300' : 'text-red-400'}`} data-testid="defense-kit-message">{msg.text}</p>}
    </div>
  );
}
